<template>
  <v-menu
    v-model="menu"
    offset-y
    :close-on-content-click="false"
    min-width="320"
  >
    <!-- 🔹 Activator -->
    <template v-slot:activator="{ on, attrs }">
      <div
        v-bind="attrs"
        v-on="on"
        class="reason-field"
      >
        {{ downtime.reasoncode }} | {{ downtime.reasonname || 'Select Reason' }}
      </div>
    </template>

    <!-- 🔹 Popup -->
    <v-card class="pa-3">

      <!-- Reason group -->
      <v-select
        dense
        outlined
        clearable
        :label="reasonGroupLabel"
        :items="reasonGroups"
        v-model="selectedReasonGroup"
        class="mt-4"
      />

      <!-- Reasons -->
      <v-autocomplete
        dense
        filled
        clearable
        label="Reason"
        return-object
        :loading="loading"
        :disabled="loading"
        :items="filteredReasons"
        item-text="reasonname"
        item-value="reasoncode"
        v-model="selectedReason"
      >
        <template #selection="data">
          {{ data.item.reasoncode }} | {{ data.item.reasonname }} |
          {{ data.item.department }} | {{ data.item.category }}
        </template>

        <template #item="data">
          <v-list-item-content>
            <v-list-item-title>
              {{ data.item.reasoncode }} | {{ data.item.reasonname }} |
              {{ data.item.category }} | {{ data.item.department }}
            </v-list-item-title>
          </v-list-item-content>
        </template>
      </v-autocomplete>

    </v-card>
  </v-menu>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { getPlannedValue } from '../utils/downtime-reason';
import { getDowntimeReasonGroupField } from '../utils/downtime-schema';

export default {
  name: 'AssignDowntime',

  props: {
    downtime: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      selectedReasonGroup: null,
      menu: false, // ✅ CONTROL MENU
    };
  },
  computed: {
    ...mapState('webApp', ['appSchema']),
    ...mapState('downtimeLog', ['downtimeReasons']),

    reasonGroupField() {
      return getDowntimeReasonGroupField(this.appSchema);
    },

    reasonGroupLabel() {
      return this.$t(`downtime.${this.reasonGroupField}`);
    },

    // ✅ Unique group values
    reasonGroups() {
      const groups = new Map();

      this.downtimeReasons.forEach((reason) => {
        const value = String(reason[this.reasonGroupField] || '').trim();

        if (value) {
          const normalizedValue = value.toLowerCase();
          if (!groups.has(normalizedValue)) {
            groups.set(normalizedValue, value);
          }
        }
      });

      return [...groups.values()];
    },

    // ✅ Filter reasons
    filteredReasons() {
      if (!this.selectedReasonGroup) return this.downtimeReasons;

      return this.downtimeReasons.filter(
        (r) => String(r[this.reasonGroupField] || '').trim().toLowerCase()
          === String(this.selectedReasonGroup || '').trim().toLowerCase(),
      );
    },

    // ✅ Selected reason
    selectedReason: {
      get() {
        return this.downtimeReasons.find(
          (r) => r.reasoncode === this.downtime.reasoncode,
        ) || null;
      },
      async set(val) {
        this.loading = true;
        let payload = {
          reasonname: '',
          reasoncode: '',
          category: '',
          department: '',

        };

        if (val) {
          const {
            reasonname, reasoncode, category, department,
          } = val;
          payload = {
            reasonname, reasoncode, category, department,
          };

          const isPlanned = getPlannedValue(val, this.downtimeReasons);

          if (isPlanned !== null) {
            payload.isPlanned = isPlanned;
          }
        }

        const records = Array.isArray(this.downtime.groupRecords)
          ? this.downtime.groupRecords
          : [this.downtime];

        await Promise.all(
          records
            .filter((record) => record && record._id)
            .map((record) => this.updateReason({
              id: record._id,
              payload,
            })),
        );

        this.loading = false;

        // ✅ AUTO CLOSE AFTER SELECT
        this.menu = false;
      },
    },
  },

  methods: {
    ...mapActions('downtimeLog', ['updateReason']),
  },
  watch: {
    downtime: {
      immediate: true,
      deep: true,
      handler() {
        this.selectedReasonGroup = this.downtime[this.reasonGroupField] || null;
      },
    },
    reasonGroupField: {
      immediate: true,
      handler() {
        this.selectedReasonGroup = this.downtime[this.reasonGroupField] || null;
      },
    },
  },
};
</script>

<style scoped>
.reason-field {
  cursor: pointer;
  padding: 4px 8px;
  border-bottom: 1px dashed #999;
  display: inline-block;
  font-size: 13px;
}
</style>
